// Header.js
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './Header.css';

function Header() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showEpubHelp, setShowEpubHelp] = useState(false);
  const [showDownloadsHelp, setShowDownloadsHelp] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="site-header">
      <div className="header-container">
        <Link to="/" className="logo">
          <img src="/logo-ck-publishing.jpg" alt="Christensen Kirtis Publishing" className="logo-image" />
          <span className="logo-text">Christensen Kirtis Publishing</span>
        </Link>

        <nav className="main-nav">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/products" className="nav-link">Products</Link>
          <Link to="/contact" className="nav-link">Contact</Link>
          
          {/* Help Menu */}
          <div className="help-menu-container">
            <button 
              className="nav-link help-menu-btn"
              onClick={() => setShowHelpMenu(!showHelpMenu)}
              onBlur={() => setTimeout(() => setShowHelpMenu(false), 150)}
            >
              Help ▾
            </button>
            {showHelpMenu && (
              <div className="help-dropdown">
                <button 
                  className="help-dropdown-item"
                  onClick={() => {
                    setShowHelpMenu(false);
                    setShowEpubHelp(true);
                  }}
                >
                  📚 What is EPUB?
                </button>
                <button 
                  className="help-dropdown-item"
                  onClick={() => {
                    setShowHelpMenu(false);
                    setShowDownloadsHelp(true);
                  }}
                >
                  📁 Find My Device Downloads
                </button>
              </div>
            )}
          </div>
        </nav>

        <div className="user-nav">
          {user ? (
            <>
              <span className="user-greeting">Hi, {user.first_name || user.email}</span>
              <Link to="/my-downloads" className="nav-link">My Downloads</Link>
              <button onClick={handleLogout} className="logout-btn">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Login</Link>
              <Link to="/register" className="register-btn">Register</Link>
            </>
          )}
        </div>
      </div>

      {/* EPUB Help Modal */}
      {showEpubHelp && (
        <div className="epub-help-modal-overlay" onClick={() => setShowEpubHelp(false)}>
          <div className="epub-help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowEpubHelp(false)}>×</button>
            <h2>📚 What is EPUB?</h2>
            <p>
              EPUB is the most widely supported e-book format. It works on almost all e-readers, 
              tablets, phones, and computers. EPUB files automatically adjust text size and layout 
              to fit your screen.
            </p>
            
            <h3>Free EPUB Readers</h3>
            
            <div className="reader-section">
              <h4>🖥️ Desktop (Windows/Mac)</h4>
              <ul>
                <li>
                  <a href="https://calibre-ebook.com/download" target="_blank" rel="noopener noreferrer">
                    Calibre
                  </a> — Free, powerful e-book manager and reader
                </li>
                <li>
                  <a href="https://www.microsoft.com/store/productId/9WZDNCRFJ4JQ" target="_blank" rel="noopener noreferrer">
                    Microsoft Edge
                  </a> — Built-in EPUB support on Windows
                </li>
                <li>
                  <a href="https://www.apple.com/apple-books/" target="_blank" rel="noopener noreferrer">
                    Apple Books
                  </a> — Pre-installed on Mac
                </li>
              </ul>
            </div>
            
            <div className="reader-section">
              <h4>📱 Android</h4>
              <ul>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.books" target="_blank" rel="noopener noreferrer">
                    Google Play Books
                  </a> — Usually pre-installed
                </li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.aldiko.android" target="_blank" rel="noopener noreferrer">
                    Aldiko Book Reader
                  </a> — Popular free reader
                </li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=org.readera" target="_blank" rel="noopener noreferrer">
                    ReadEra
                  </a> — Ad-free, supports many formats
                </li>
              </ul>
            </div>
            
            <div className="reader-section">
              <h4>🍎 iPhone/iPad</h4>
              <ul>
                <li>
                  <a href="https://apps.apple.com/app/apple-books/id364709193" target="_blank" rel="noopener noreferrer">
                    Apple Books
                  </a> — Pre-installed on all iOS devices
                </li>
                <li>
                  <a href="https://apps.apple.com/app/kybook-3-ebook-reader/id1348198785" target="_blank" rel="noopener noreferrer">
                    KyBook 3
                  </a> — Feature-rich free reader
                </li>
              </ul>
            </div>
            
            <p className="help-tip">
              <strong>Tip:</strong> After downloading the EPUB file, open it with your preferred reader app. 
              Most devices will automatically suggest an app when you tap the downloaded file.
            </p>
          </div>
        </div>
      )}

      {/* Find Downloads Help Modal */}
      {showDownloadsHelp && (
        <div className="epub-help-modal-overlay" onClick={() => setShowDownloadsHelp(false)}>
          <div className="epub-help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDownloadsHelp(false)}>×</button>
            <h2>📁 Finding Your Downloads</h2>
            <p>
              After downloading an EPUB file, you'll need to locate it on your device to open it with a reader app.
              Here's how to find your Downloads folder on different devices:
            </p>
            
            <div className="reader-section">
              <h4>🖥️ Windows PC</h4>
              <ol>
                <li>Open <strong>File Explorer</strong> (folder icon on taskbar, or press <code>Win + E</code>)</li>
                <li>Click <strong>Downloads</strong> in the left sidebar</li>
                <li>Or navigate to: <code>C:\Users\YourName\Downloads</code></li>
              </ol>
              <p className="help-tip">
                <strong>Quick access:</strong> Press <code>Ctrl + J</code> in your browser to open the downloads list directly.
              </p>
            </div>
            
            <div className="reader-section">
              <h4>🍎 Mac</h4>
              <ol>
                <li>Open <strong>Finder</strong> (smiley face icon in dock)</li>
                <li>Click <strong>Downloads</strong> in the left sidebar</li>
                <li>Or press <code>Option + Command + L</code> to go directly to Downloads</li>
              </ol>
              <p className="help-tip">
                <strong>Quick access:</strong> Press <code>Command + Option + L</code> in Safari, or check the Downloads icon in your dock.
              </p>
            </div>
            
            <div className="reader-section">
              <h4>📱 iPhone / iPad</h4>
              <ol>
                <li>Open the <strong>Files</strong> app (blue folder icon)</li>
                <li>Tap <strong>Browse</strong> at the bottom</li>
                <li>Tap <strong>On My iPhone</strong> (or iPad)</li>
                <li>Look in the <strong>Downloads</strong> folder</li>
              </ol>
              <p className="help-tip">
                <strong>Alternative:</strong> After downloading, tap the download arrow icon (↓) in Safari's address bar to see recent downloads.
              </p>
              <p>
                <a href="https://support.apple.com/en-us/HT206481" target="_blank" rel="noopener noreferrer">
                  Apple Support: Using the Files app →
                </a>
              </p>
            </div>
            
            <div className="reader-section">
              <h4>🤖 Android</h4>
              <ol>
                <li>Open the <strong>Files</strong> app (or "My Files" on Samsung)</li>
                <li>Tap <strong>Downloads</strong> or <strong>Download history</strong></li>
                <li>Your EPUB file should appear at the top</li>
              </ol>
              <p className="help-tip">
                <strong>Alternative:</strong> Pull down the notification shade and tap the "Download complete" notification.
              </p>
              <p>
                <a href="https://support.google.com/files/answer/10219944" target="_blank" rel="noopener noreferrer">
                  Google Support: Find & delete files →
                </a>
              </p>
            </div>
            
            <div className="reader-section">
              <h4>💡 Still Can't Find It?</h4>
              <ul>
                <li>Check your browser's download history (<code>Ctrl/Cmd + J</code>)</li>
                <li>Search your device for ".epub" files</li>
                <li>Make sure the download completed (look for a checkmark, not a spinning icon)</li>
                <li>Try downloading again - sometimes downloads fail silently</li>
              </ul>
            </div>
            
            <p className="help-tip">
              <strong>Next step:</strong> Once you find your EPUB file, tap or double-click it to open with your e-reader app.
              See "What is EPUB?" in the Help menu for recommended reader apps.
            </p>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
